// A single-line input removes line breaks but leaves each line's indentation.
// Recover those boundaries only when the retained indentation and Python suite
// boundaries agree. Already multiline code and ambiguous input stay untouched.
const restoreCollapsedPythonBody = (code) => {
  if (
    /[\r\n]/.test(code) ||
    !/^(?:async\s+)?def\s+\w+\s*\(|^class\s+\w+/.test(code)
  ) {
    return code;
  }

  const lines = [];
  const brackets = [];
  const indents = [0];
  let quote = null;
  let lineStart = 0;
  let lineIndent = 0;
  let cursor = 0;

  while (cursor < code.length) {
    const char = code[cursor];
    if (quote) {
      if (char === "\\") {
        cursor += 2;
      } else if (code.startsWith(quote, cursor)) {
        cursor += quote.length;
        quote = null;
      } else {
        cursor += 1;
      }
      continue;
    }

    if (char === '"' || char === "'") {
      quote = code.startsWith(char.repeat(3), cursor) ? char.repeat(3) : char;
      cursor += quote.length;
      continue;
    }
    // With no newline left, a comment's original end is unknowable.
    if (char === "#") return code;
    if (["(", "[", "{"].includes(char)) brackets.push(char);
    if (")]}".includes(char)) {
      if (brackets.pop() !== { ")": "(", "]": "[", "}": "{" }[char])
        return code;
    }

    if (char !== " " || brackets.length) {
      cursor += 1;
      continue;
    }

    let end = cursor;
    while (code[end] === " ") end += 1;
    const gap = end - cursor;
    const previous = code.slice(lineStart, cursor).trimEnd();
    const next = code.slice(end);
    const startsStatement =
      /^(?:[A-Za-z_]\w*(?:\.[A-Za-z_]\w*)*\s*(?:=(?!=)|\+=|-=|\()|(?:return|raise|pass|break|continue|assert|import|from|del|global|nonlocal)\b)/.test(
        next,
      ) ||
      /^(?:(?:if|elif|while)\s+(?:not\s+)?[A-Za-z_]\w*(?:\.[A-Za-z_]\w*)*|else|try|finally): {5,}/.test(
        next,
      );

    if (gap >= 5) {
      if (
        (gap - 1) % 4 !== 0 ||
        !startsStatement ||
        /[,=+\-*/([{]$/.test(previous)
      )
        return code;
      const nextIndent = gap - 1;
      const currentIndent = indents[indents.length - 1];
      if (previous.endsWith(":")) {
        if (nextIndent !== currentIndent + 4) return code;
        indents.push(nextIndent);
      } else {
        if (!lines.length || nextIndent > currentIndent) return code;
        while (indents[indents.length - 1] > nextIndent) indents.pop();
        if (indents[indents.length - 1] !== nextIndent) return code;
      }
      lines.push(" ".repeat(lineIndent) + previous);
      lineIndent = nextIndent;
      lineStart = end;
    }
    cursor = end;
  }

  if (quote || brackets.length || !lines.length) return code;
  const lastLine = code.slice(lineStart);
  if (lastLine.trimEnd().endsWith(":")) return code;
  lines.push(" ".repeat(lineIndent) + lastLine);
  return lines.join("\n");
};

export const restoreCollapsedPython = (source) => {
  const body = source.trim();
  const restored = restoreCollapsedPythonBody(body);
  if (restored === body) return source;
  const start = source.indexOf(body);
  const prefix = source.slice(0, start);
  // Leading indentation could belong to an enclosing block that is missing.
  if (prefix.split(/\r?\n/).pop()) return source;
  return prefix + restored + source.slice(start + body.length);
};
